import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { supabase } from "../lib/supabase";
import { Student, Run } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import { ENTITY_NAMES } from "../constants/entities";
import StudentsList from "../components/students/StudentsList";
import { isRunCompleted } from "../utils/runs";
// import StudentsList from "../components/students/StudentsTable";

type SortField = "studentName" | "parentName" | "age" | "series" | "run";
type SortDirection = "asc" | "desc";

interface StudentWithRun extends Student {
  run?: {
    id: string;
    name: string;
    day_of_week: string[];
    start_time: string;
    lab_dates?: Array<{
      id: string;
      run_id: string;
      lab_number: number;
      date: string;
    }>;
    series: {
      id: string;
      title: string;
      code: string;
    };
  };
}

interface RunWithSeries extends Run {
  series?: {
    title: string;
    code: string;
  };
}

const Students: React.FC = () => {
  const [students, setStudents] = useState<StudentWithRun[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedRun, setSelectedRun] = useState<string | null>(null);
  const [runOptions, setRunOptions] = useState<RunWithSeries[]>([]);
  const [enrollmentStatus, setEnrollmentStatus] = useState<
    "all" | "enrolled" | "unenrolled"
  >("all");
  const { userRole } = useAuth();
  const [sortField, setSortField] = useState<SortField>("studentName");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const isAdmin = userRole === "admin";
  const studentsPerPage = 50;

  useEffect(() => {
    fetchStudents();
    fetchFilterOptions();
  }, [
    currentPage,
    searchTerm,
    selectedRun,
    enrollmentStatus,
    sortField,
    sortDirection,
  ]);

  const fetchStudents = async () => {
    try {
      setIsLoading(true);

      let query = supabase.from("students").select(
        `
          *,
          run:run_id (
            id,
            name,
            day_of_week,
            start_time,
            lab_dates (
              id,
              run_id,
              lab_number,
              date
            ),
            series:series_id (
              code,
              id,
              title
            )
          )
        `,
        { count: "exact" }
      );

      if (searchTerm) {
        query = query.or(
          `codername.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,parentname.ilike.%${searchTerm}%`
        );
      }

      if (selectedRun) {
        query = query.eq("run_id", selectedRun);
      }
      // Note: enrollment status filtering will be done client-side
      // after fetching data to check if runs are completed

      // Apply sorting
      switch (sortField) {
        case "studentName":
          query = query.order("codername", {
            ascending: sortDirection === "asc",
          });
          break;
        case "parentName":
          query = query.order("parentname", {
            ascending: sortDirection === "asc",
          });
          break;
        case "age":
          query = query.order("age", {
            ascending: sortDirection === "asc",
          });
          break;
      }

      // Calculate pagination (skip pagination if we need to do client-side enrollment filtering)
      const needsClientSideFiltering =
        !selectedRun && enrollmentStatus !== "all";

      let finalQuery = query;
      if (!needsClientSideFiltering) {
        const from = (currentPage - 1) * studentsPerPage;
        const to = from + studentsPerPage - 1;
        finalQuery = query.range(from, to);
      }

      // Execute query
      const { data, error, count } = await finalQuery;

      if (error) throw error;

      let processedData = data as StudentWithRun[];

      // Apply enrollment status filtering client-side (after fetching run data)
      if (needsClientSideFiltering) {
        processedData = processedData.filter((student) => {
          const hasRun = !!student.run;

          if (!hasRun) {
            // Student has no run - always considered "unenrolled"
            return enrollmentStatus === "unenrolled";
          }

          // Student has a run - check if it's completed
          const isCompleted = isRunCompleted({
            active: true, // Assume active since student is enrolled
            lab_dates: student.run?.lab_dates || [],
          });

          if (enrollmentStatus === "enrolled") {
            // "Enrolled" means: has a run AND that run is not completed
            return !isCompleted;
          } else {
            // "Unenrolled" means: no run OR run is completed
            return isCompleted;
          }
        });

        // Apply client-side pagination to filtered data
        const from = (currentPage - 1) * studentsPerPage;
        const to = from + studentsPerPage;
        processedData = processedData.slice(from, to);
      }

      // Handle client-side sorting for nested fields if needed
      if (sortField === "series" || sortField === "run") {
        processedData = processedData.sort((a, b) => {
          const multiplier = sortDirection === "asc" ? 1 : -1;
          if (sortField === "series") {
            const seriesA = a.run?.series?.title || "";
            const seriesB = b.run?.series?.title || "";
            return multiplier * seriesA.localeCompare(seriesB);
          } else {
            const runA = a.run?.name || "";
            const runB = b.run?.name || "";
            return multiplier * runA.localeCompare(runB);
          }
        });
      }

      setStudents(processedData);

      // Calculate total pages based on whether we did client-side filtering
      if (needsClientSideFiltering) {
        // For client-side filtering, we need to count all filtered results
        // First, get the total filtered count
        let allFilteredData = data as StudentWithRun[];
        allFilteredData = allFilteredData.filter((student) => {
          const hasRun = !!student.run;

          if (!hasRun) {
            return enrollmentStatus === "unenrolled";
          }

          const isCompleted = isRunCompleted({
            active: true,
            lab_dates: student.run?.lab_dates || [],
          });

          if (enrollmentStatus === "enrolled") {
            return !isCompleted;
          } else {
            return isCompleted;
          }
        });

        setTotalPages(Math.ceil(allFilteredData.length / studentsPerPage));
      } else {
        setTotalPages(Math.ceil((count || 0) / studentsPerPage));
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      toast.error("Failed to load students");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const { data: runData, error: runError } = await supabase
        .from("runs")
        .select(
          `
          *,
          series:series_id (
            title,
            code
          )
        `
        )
        .order("day_of_week")
        .order("start_time");

      if (runError) throw runError;
      setRunOptions(runData || []);
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  };

  const handleDeleteStudent = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this student?")) {
      try {
        const { error } = await supabase.from("students").delete().eq("id", id);
        if (error) throw error;
        setStudents(students.filter((student) => student.id !== id));
        toast.success("Student deleted successfully");
      } catch (error) {
        console.error("Error deleting student:", error);
        toast.error("Failed to delete student");
      }
    }
  };

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedRun(null);
    setEnrollmentStatus("all");
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    const validField = field as SortField;
    if (sortField === validField) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(validField);
      setSortDirection("asc");
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <h1 className="">Students</h1>
        <Link to="/students/add" className="btn btn-primary mt-4 sm:mt-0">
          <Plus className="h-5 w-5" />
          Add Student
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <div className="p-4 border-b border-neutral-200">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-neutral-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 py-2 border border-neutral-300 rounded-md text-sm"
                placeholder="Search by name, email or parent..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative">
                <select
                  className="appearance-none block w-full pl-3 pr-10 py-2 border border-neutral-300 rounded-md bg-white text-sm text-neutral-700"
                  value={selectedRun || ""}
                  onChange={(e) => {
                    setSelectedRun(e.target.value || null);
                    setEnrollmentStatus("all");
                    setCurrentPage(1);
                  }}
                >
                  <option value="">{ENTITY_NAMES.RUN}</option>
                  {runOptions.map((run) => (
                    <option key={run.id} value={run.id}>
                      {ENTITY_NAMES.RUN} {run.name} of{" "}
                      {run.series?.code || "Unknown Series"}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  <ChevronLeft className="h-4 w-4 text-neutral-500" />
                </div>
              </div>

              <div className="relative">
                <select
                  className="appearance-none block w-full pl-3 pr-10 py-2 border border-neutral-300 rounded-md bg-white text-sm text-neutral-700"
                  value={enrollmentStatus}
                  onChange={(e) => {
                    setEnrollmentStatus(
                      e.target.value as "all" | "enrolled" | "unenrolled"
                    );
                    setSelectedRun(null);
                    setCurrentPage(1);
                  }}
                >
                  <option value="all">Status</option>
                  <option value="enrolled">Enrolled Students</option>
                  <option value="unenrolled">Unenrolled Students</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  <ChevronLeft className="h-4 w-4 text-neutral-500" />
                </div>
              </div>

              {(searchTerm || selectedRun || enrollmentStatus !== "all") && (
                <button onClick={resetFilters} className="btn btn-outline">
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="p-8 text-center">
            <div className="text-neutral-500">Loading students...</div>
          </div>
        ) : (
          <StudentsList
            students={students}
            isAdmin={isAdmin}
            onDeleteStudent={handleDeleteStudent}
            handleSort={handleSort}
            sortField={sortField}
            sortDirection={sortDirection}
          />
        )}

        {totalPages > 1 && (
          <div className="px-6 py-3 flex items-center justify-between border-t border-neutral-200 bg-neutral-50">
            <div className="text-sm text-neutral-700">
              Showing page {currentPage} of {totalPages}
            </div>
            <div className="flex-1 flex justify-end">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-2 py-2 rounded-md bg-white text-sm font-medium ${
                  currentPage === 1
                    ? "text-neutral-300 cursor-not-allowed"
                    : "text-neutral-700 hover:bg-neutral-100"
                }`}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className={`ml-3 relative inline-flex items-center px-2 py-2 rounded-md bg-white text-sm font-medium ${
                  currentPage === totalPages
                    ? "text-neutral-300 cursor-not-allowed"
                    : "text-neutral-700 hover:bg-neutral-100"
                }`}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Students;
